import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { useT } from "../lib/i18n";

const FEATURES = {
  free: ["1,000 emails every month", "Campaigns, contacts and AI drafts", "Send from your own domain", "Honest delivery reports"],
  pro: ["50,000 emails every month", "Split tests and open & click reports", "Your whole team, with roles", "Priority deliverability help"],
  scale: ["250,000 emails every month", "Everything in Pro", "Higher sending speed for big launches", "Personal review of your limits"],
};
const fmtEur = (n) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);

function useCheckout(current, onChanged) {
  const { user, can } = useAuth();
  const { t } = useT();
  const [busy, setBusy] = useState(null);
  const start = async (plan, volume) => {
    if (!user) { window.location.href = "/register"; return; }
    if (!can("owner")) { toast.error(t("Only workspace owners can change the plan")); return; }
    const switching = current && current !== "free";
    if (switching && !window.confirm(t("Change your subscription now? Stripe charges or credits the prorated difference immediately."))) return;
    setBusy(plan);
    try {
      const { data } = await api.post("/app/billing/checkout", { plan, volume });
      if (data.changed) { toast.success(t("Plan changed")); setBusy(null); onChanged?.(); return; }
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const cancel = async () => {
    if (!window.confirm(t("Cancel your subscription? You keep your plan until the end of the paid period, then return to Free."))) return;
    setBusy("free");
    try { await api.post("/app/billing/cancel"); toast.success(t("Subscription will end at the period end")); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return { busy, start, cancel, busyLabel: current && current !== "free" ? "Updating…" : "Redirecting…" };
}

function CustomCard({ cfg, onBuy, busy, busyLabel, currentVolume, paid }) {
  const { t, lang } = useT();
  const [volume, setVolume] = useState(currentVolume || 500000);
  const isCurrent = !!currentVolume && volume === currentVolume;
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    const h = setTimeout(() => api.get("/public/plans/quote", { params: { volume } }).then((r) => setQuote(r.data)).catch(() => setQuote(null)), 150);
    return () => clearTimeout(h);
  }, [volume]);
  const pct = ((volume - cfg.min) / (cfg.max - cfg.min)) * 100;
  return (
    <div className="lux-border rounded-3xl p-8 lg:col-span-3" data-testid="pricing-custom">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-md">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-300">Custom</div>
          <h3 className="mt-3 text-2xl font-semibold text-white">{t("Pay for exactly the volume you need")}</h3>
          <p className="mt-2 text-sm text-zinc-400">{t("€1.00 per 1,000 emails at 10k, sliding down to €0.50 per 1,000 from 1M emails a month.")}</p>
        </div>
        <div className="flex-1 lg:max-w-xl">
          <div className="mb-4 flex items-end justify-between">
            <div><div className="font-display text-4xl font-semibold text-white" data-testid="custom-volume">{volume.toLocaleString(lang === "fi" ? "fi-FI" : "en-IE")}</div><div className="text-xs text-zinc-500">{t("emails / month")}</div></div>
            <div className="text-right"><div className="font-display text-4xl font-semibold text-white" data-testid="custom-price">{quote ? fmtEur(quote.price) : "—"}</div>
              <div className="text-xs text-zinc-500">{quote ? `€${quote.rate_per_1000.toFixed(2)} / 1 000 · ${t("per month")}` : ""}</div></div>
          </div>
          <input type="range" className="lux-range w-full" min={cfg.min} max={cfg.max} step={cfg.step} value={volume} style={{ "--p": `${pct}%` }}
            onChange={(e) => setVolume(Number(e.target.value))} aria-label="Monthly email volume" data-testid="custom-volume-slider" />
          <div className="mt-2 flex justify-between text-[11px] text-zinc-600"><span>10k</span><span>1M</span><span>2M</span></div>
          <Button className="glow-btn mt-6 w-full rounded-full" disabled={busy === "custom" || !quote || isCurrent} onClick={() => onBuy("custom", volume)} data-testid="pricing-buy-custom">
            {customLabel({ isCurrent, busy: busy === "custom", busyLabel: t(busyLabel), paid, quote, t })}</Button>
        </div>
      </div>
    </div>
  );
}

function customLabel({ isCurrent, busy, busyLabel, paid, quote, t }) {
  if (isCurrent) return t("Current plan");
  if (busy) return busyLabel;
  return `${t(paid ? "Switch to" : "Subscribe for")} ${quote ? fmtEur(quote.price) : "…"} ${t("/ month")}`;
}

const OUTLINE = "w-full rounded-full border-white/15 bg-transparent hover:bg-white/5 hover:text-white";

function PlanAction({ p, featured, isCurrent, current, canceling, busy, busyLabel, start, cancel }) {
  const { t } = useT();
  if (isCurrent) return <Button disabled className="w-full rounded-full" variant="outline" data-testid={`pricing-current-${p.id}`}>{t("Current plan")}</Button>;
  if (p.id === "free" && current) {
    return <Button variant="outline" className={OUTLINE} disabled={canceling || busy === "free"} onClick={cancel} data-testid="pricing-cancel-subscription">
      {t(canceling ? "Downgrade scheduled" : "Cancel to downgrade")}</Button>;
  }
  if (p.id === "free") return <Button asChild variant="outline" className={OUTLINE}><Link to="/register">{t("Start free")}</Link></Button>;
  return <Button className={`w-full rounded-full ${featured ? "glow-btn" : ""}`} variant={featured ? "default" : "outline"} disabled={busy === p.id} onClick={() => start(p.id)} data-testid={`pricing-buy-${p.id}`}>
    {busy === p.id ? t(busyLabel) : `${t("Choose")} ${p.name}`}</Button>;
}

export default function PricingTable({ current, currentVolume, canceling, onChanged }) {
  const { t } = useT();
  const [cfg, setCfg] = useState(null);
  const { busy, start, cancel, busyLabel } = useCheckout(current, onChanged);
  useEffect(() => { api.get("/public/plans").then((r) => setCfg(r.data)).catch((e) => console.warn("Could not load plans", e)); }, []);
  if (!cfg) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="pricing-table">
      {cfg.plans.map((p) => {
        const featured = p.id === "pro";
        const isCurrent = current === p.id;
        return (
          <div key={p.id} className={`lux-border relative flex flex-col rounded-3xl p-8 ${featured ? "bg-purple-950/20 shadow-[0_30px_80px_-30px_rgba(147,51,234,0.6)]" : ""}`} data-testid={`pricing-plan-${p.id}`}>
            {featured && <span className="absolute -top-3 left-8 rounded-full bg-purple-600 px-3 py-1 text-[11px] font-semibold text-white">{t("Most popular")}</span>}
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-300">{p.name}</div>
            <div className="mt-5 flex items-baseline gap-1"><span className="font-display text-5xl font-semibold text-white">{fmtEur(p.price)}</span><span className="text-sm text-zinc-500">{t("/ month")}</span></div>
            <ul className="my-8 space-y-3 text-sm text-zinc-300">{FEATURES[p.id].map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-purple-400" />{t(f)}</li>)}</ul>
            <div className="mt-auto">
              <PlanAction p={p} featured={featured} isCurrent={isCurrent} current={current} canceling={canceling} busy={busy} busyLabel={busyLabel} start={start} cancel={cancel} />
            </div>
          </div>
        );
      })}
      <CustomCard cfg={cfg.custom} onBuy={start} busy={busy} busyLabel={busyLabel} currentVolume={current === "custom" ? currentVolume : null} paid={!!current && current !== "free"} />
      <p className="text-xs text-zinc-500 lg:col-span-3">Prices in EUR, billed monthly via Stripe; tax is calculated at checkout. Plans set your workspace's sending limits; AWS SES account limits also apply.</p>
    </div>
  );
}
